import {
  GridRole,
  MatchTargetKey,
  SourceKey,
  SourceTag,
} from './source-key.model'
import { ColumnType } from './field-catalog.model'

/** Column-source groups used to organise columns in the Column Manager. */
export type ColSourceGroup =
  | 'Helios'
  | 'Sec Master'
  | 'ELM'
  | 'External'
  | 'Default'
  | 'Trades'
  | 'Cash Activity'
  | 'Aggregate Info'

/**
 * The group the UI's computed leading columns (Breaks, Source, Status, match counts, Account ID,
 * Comments) belong to. The API returns its own equivalents under the HELIOS group key; the UI
 * builds these itself, so the label is asserted here rather than read off a catalog entry.
 */
export const HELIOS_GROUP: ColSourceGroup = 'Helios'

/**
 * The group Aggregate Info columns belong to. Columns in this group carry a value inside the row's
 * `aggregateInfo` JSON blob keyed by `field` verbatim — the ColDef factory reads them via
 * `row.aggregateInfo[field]` rather than off the top-level `fields` map. The API returns these
 * under the AGGREGATE_INFO group key; runtime-discovered columns from unmodeled blob keys are also
 * attributed here. Driving the value accessor off this group key keeps `field` un-prefixed, which
 * the catalog contract requires ("field is the only key to join on — do not prefix it").
 */
export const AGGREGATE_INFO_GROUP: ColSourceGroup = 'Aggregate Info'

export type CatalogCellType =
  | 'text'
  | 'number'
  | 'money'
  | 'date'
  | 'datetime'
  | 'boolean'
  | 'set'
  /** A `TEXT_LIST` column: one cell holds a list of ids. */
  | 'list'

/** One catalog column: a data-driven description that the ColDef factory turns into an ag-Grid col. */
export interface ColumnCatalogEntry {
  field: string
  headerName: string
  colSource: ColSourceGroup
  cellType: CatalogCellType
  /**
   * Whether this column's value lives in a JSONB blob rather than a promoted SQL column, straight
   * off the catalog's `ColumnDescriptor.isDynamic`.
   *
   * **The filter/sort routing contract** (PVAL-26129 §3): `true` sends the column's filter under
   * `filter.dynamicFilters`, anything else under `filter`. Required rather than optional on purpose
   * — every construction site has to decide, because a wrong answer is silent in both directions
   * (a dynamic key at the top level is ignored with no error; a static name under `dynamicFilters`
   * is a 400).
   *
   * Catalog-less UI-computed columns have no entry here at all, which is what keeps them out of the
   * request; see `CLIENT_ONLY_FIELDS` for the ones a catalog entry of the same name would otherwise
   * let through.
   */
  isDynamic: boolean
  /**
   * The catalog's own value type, carried verbatim.
   *
   * {@link cellType} is a RENDERING hint and is lossy for this purpose — it collapses NUM and MONEY,
   * so deriving a sort `type` from it would send `NUM` for a money column and `TEXT` for a `set`.
   * The request builder reads this instead (PVAL-26129 §5: "every other column uses its catalog type
   * verbatim"). Optional because the in-repo fixture catalogs describe columns by cellType only; the
   * request builder falls back to a cellType-derived type when it is absent.
   */
  valueType?: ColumnType
  /** Hidden by default (e.g. Aggregate Info columns). */
  hide?: boolean
  width?: number
}

/** Everything the workbench needs to know about a source: metadata + column catalog. */
export interface SourceDefinition {
  key: SourceKey
  label: string
  tag: SourceTag
  tagColor: string
  color: string
  model: string
  sub: string
  allowedRoles: GridRole[]
  /**
   * Roles where the source is listed in the picker but cannot be chosen. Used for pairings the
   * workbench is designed for but not yet cleared to show: the option stays visible so the
   * eventual capability is discoverable, and greyed out so nobody lands on unfinished data.
   *
   * Must be a subset of {@link allowedRoles} — a role that is not allowed is not rendered at all,
   * so listing it here would be silently meaningless.
   */
  disabledRoles?: GridRole[]
  matchTargets: MatchTargetKey[]
  /** Prometheus expands its AggregateInformation JSON into discovered columns at runtime. */
  hasAggregateInfo?: boolean
  /** Whether a status quick-filter (Unmatched/Ignored/Matched) is shown for this source. */
  hasStatusFilter?: boolean
  /** Whether this source exposes the Comments column + View/Add actions. */
  hasComments?: boolean
  /** Whether this source can be posted to. Prometheus is read-only, so it has no Create action. */
  hasCreate?: boolean
  /**
   * Whether Create opens a menu rather than firing a single action. Create asks what KIND of
   * document you are making first: Clearwater lists its posting types, Enfusion the curated
   * tran categories (Listed Trade / GL Entry). Both postable sources carry a menu as of PVAL-26341 — see
   * the prototype's `createLoadoutItems`, `docs/prototypes/pval-26341/Transactions Panel
   * 2.0.dc.html:3400-3411`.
   *
   * Optional, and its absence means "one plain button": that is the shape a source added before its
   * create flow has been designed gets, which is what Enfusion looked like until the tran category menu
   * was wired. It is not a fallback any current source reaches.
   */
  hasCreateMenu?: boolean
}
